/**
 * Sparkles — a one-shot burst of colored dots that fly outward and fade.
 * Mount it with a changing `key` to retrigger the burst (e.g. on equip).
 */

import { useEffect, useRef } from 'react';
import { Animated, Easing, View } from 'react-native';
import { darkTheme } from '../../../src/theme/themes';
import { sparklesStyles as styles } from './Sparkles.styles';

const COLORS = [darkTheme.accent, darkTheme.primary, darkTheme.secondary, darkTheme.white];
const COUNT = 14;

/** Deterministic burst geometry (angle, distance, size, color), varied by index. */
const PARTICLES = Array.from({ length: COUNT }, (_, i) => {
  const angle = (i / COUNT) * Math.PI * 2 + (i % 3) * 0.18;
  const distance = 60 + (i % 5) * 14;
  const size = 5 + (i % 4) * 2;
  return {
    tx: Math.cos(angle) * distance,
    ty: Math.sin(angle) * distance,
    size,
    color: COLORS[i % COLORS.length],
  };
});

export function Sparkles() {
  const t = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    t.setValue(0);
    Animated.timing(t, {
      toValue: 1,
      duration: 640,
      easing: Easing.bezier(0.16, 1, 0.3, 1),
      useNativeDriver: true,
    }).start();
  }, [t]);

  const opacity = t.interpolate({ inputRange: [0, 0.8, 1], outputRange: [1, 0.6, 0] });
  const scale = t.interpolate({ inputRange: [0, 1], outputRange: [1, 0.2] });

  return (
    <View style={styles.root} pointerEvents="none">
      {PARTICLES.map((p, i) => (
        <Animated.View
          key={i}
          style={[
            styles.particle,
            {
              width: p.size,
              height: p.size,
              borderRadius: p.size / 2,
              backgroundColor: p.color,
              opacity,
              transform: [
                { translateX: t.interpolate({ inputRange: [0, 1], outputRange: [0, p.tx] }) },
                { translateY: t.interpolate({ inputRange: [0, 1], outputRange: [0, p.ty] }) },
                { scale },
              ],
            },
          ]}
        />
      ))}
    </View>
  );
}
